import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Slider,
  Menu,
  MenuItem,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Close,
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
  Fullscreen,
  FullscreenExit,
  Replay,
  Speed,
  ErrorOutline,
  Forward10,
  Replay10,
} from '@mui/icons-material';
import { api } from '../context/AuthContext';

const BRAND = '#1E3A8A';
const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

function formatTime(sec) {
  if (!isFinite(sec) || sec < 0) return '0:00';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function logSeekableRanges(video) {
  try {
    const ranges = [];
    for (let i = 0; i < video.seekable.length; i++) {
      ranges.push({ start: video.seekable.start(i), end: video.seekable.end(i) });
    }
    console.log('[RecordingPlayer] seekable ranges', ranges);
  } catch (err) {
    console.log('[RecordingPlayer] seekable ranges unavailable:', err.message);
  }
}

export default function RecordingPlayerModal({ recordingId, open, onClose }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  const [playbackUrl, setPlaybackUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffering, setBuffering] = useState(false);
  const [ended, setEnded] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [speed, setSpeed] = useState(1);
  const [speedMenuAnchor, setSpeedMenuAnchor] = useState(null);
  const [hasRetriedExpiredUrl, setHasRetriedExpiredUrl] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const durationFixInProgressRef = useRef(false);

  const fetchPlaybackUrl = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/interview-recordings/${recordingId}/playback-url`);
      setPlaybackUrl(res.data?.data?.url || null);
    } catch (err) {
      console.error('Failed to fetch playback URL:', err);
      setError('This recording could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [recordingId]);

  useEffect(() => {
    if (open && recordingId) {
      setHasRetriedExpiredUrl(false);
      setEnded(false);
      fetchPlaybackUrl();
    }
  }, [open, recordingId, fetchPlaybackUrl]);

  // Keep fullscreen state in sync whether exited via our own button, the
  // browser's native fullscreen UI, or the ESC key.
  useEffect(() => {
    const onFullscreenChange = () => {
      const active = document.fullscreenElement === containerRef.current;
      setIsFullscreen(active);
      console.log(active ? '[RecordingPlayer] fullscreen entered' : '[RecordingPlayer] fullscreen exited');
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  // Closing the modal must not leave the browser stuck in fullscreen.
  useEffect(() => {
    if (!open && document.fullscreenElement === containerRef.current && document.exitFullscreen) {
      document.exitFullscreen();
    }
  }, [open]);

  const handleVideoError = () => {
    if (!hasRetriedExpiredUrl) {
      // Presigned URL may have expired between fetch and playback — retry once.
      setHasRetriedExpiredUrl(true);
      fetchPlaybackUrl();
    } else {
      setError('Unable to play this recording. The link may have expired.');
    }
  };

  // MediaRecorder-produced WebM commonly reports duration=Infinity because no
  // duration/index metadata is written for a live-recorded stream. Force the
  // browser to compute the real duration by seeking near the end once (S3
  // supports HTTP range requests, so this doesn't download the whole file),
  // then seek back to 0. This reads the true duration — it does not fake it.
  const fixDurationIfNeeded = useCallback((video) => {
    if (!video || durationFixInProgressRef.current) return;
    if (Number.isFinite(video.duration) && video.duration > 0) {
      setDuration(video.duration);
      console.log('[RecordingPlayer] duration', video.duration);
      logSeekableRanges(video);
      return;
    }

    console.log('[RecordingPlayer] duration reported as non-finite, forcing browser duration discovery');
    durationFixInProgressRef.current = true;

    const onTimeUpdate = () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      const fixedDuration = video.duration;
      console.log('[RecordingPlayer] duration', fixedDuration);
      logSeekableRanges(video);
      if (Number.isFinite(fixedDuration) && fixedDuration > 0) {
        setDuration(fixedDuration);
      }
      video.currentTime = 0;
      durationFixInProgressRef.current = false;
    };

    video.addEventListener('timeupdate', onTimeUpdate);
    try {
      video.currentTime = 1e101; // forces the browser to seek near the true end
    } catch {
      video.removeEventListener('timeupdate', onTimeUpdate);
      durationFixInProgressRef.current = false;
    }
  }, []);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) { video.play(); } else { video.pause(); }
  };

  const handleSeek = (event, value) => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(value)) return;
    const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : (Number.isFinite(video.duration) ? video.duration : 0);
    const clamped = Math.min(Math.max(0, value), safeDuration || 0);
    console.log(`[RecordingPlayer] seek requested to=${clamped}`);
    video.currentTime = clamped;
    setCurrentTime(clamped);
  };

  const skip = (delta) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.min(Math.max(0, video.currentTime + delta), video.duration || 0);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  };

  const handleVolumeChange = (event, value) => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = value;
    setVolume(value);
    if (value === 0) { video.muted = true; setMuted(true); } else if (muted) { video.muted = false; setMuted(false); }
  };

  const handleSpeedSelect = (s) => {
    const video = videoRef.current;
    if (video) video.playbackRate = s;
    setSpeed(s);
    setSpeedMenuAnchor(null);
  };

  const handleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      if (document.exitFullscreen) document.exitFullscreen();
    } else if (el.requestFullscreen) {
      el.requestFullscreen();
    }
  };

  const handleReplay = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    video.play();
    setEnded(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px', overflow: 'hidden' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, bgcolor: BRAND }}>
        <Typography sx={{ color: '#fff', fontWeight: 600 }}>Interview Recording</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 0, bgcolor: '#000' }}>
        <Box ref={containerRef} sx={{ position: 'relative', width: '100%', bgcolor: '#000' }}>
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
              <CircularProgress sx={{ color: '#fff' }} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 400, color: '#fff' }}>
              <ErrorOutline sx={{ fontSize: 48, mb: 1 }} />
              <Typography>{error}</Typography>
            </Box>
          )}

          {!loading && !error && playbackUrl && (
            <>
              <video
                ref={videoRef}
                src={playbackUrl}
                style={{ width: '100%', maxHeight: '70vh', display: 'block', backgroundColor: '#000' }}
                onClick={togglePlay}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => { setPlaying(false); setEnded(true); }}
                onWaiting={() => setBuffering(true)}
                onCanPlay={() => setBuffering(false)}
                onLoadedMetadata={(e) => {
                  console.log('[RecordingPlayer] loadedmetadata');
                  fixDurationIfNeeded(e.target);
                }}
                onDurationChange={(e) => {
                  if (Number.isFinite(e.target.duration) && e.target.duration > 0) {
                    console.log('[RecordingPlayer] duration', e.target.duration);
                    setDuration(e.target.duration);
                  }
                }}
                onTimeUpdate={(e) => { if (!durationFixInProgressRef.current) setCurrentTime(e.target.currentTime); }}
                onSeeked={() => console.log('[RecordingPlayer] seek completed')}
                onError={handleVideoError}
              />

              {isFullscreen && (
                <Tooltip title="Exit Fullscreen">
                  <IconButton
                    onClick={handleFullscreen}
                    sx={{
                      position: 'absolute', top: 12, right: 12, zIndex: 2,
                      color: '#fff', bgcolor: 'rgba(0,0,0,0.55)',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                    }}
                  >
                    <FullscreenExit />
                  </IconButton>
                </Tooltip>
              )}

              {buffering && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                  <CircularProgress sx={{ color: '#fff' }} />
                </Box>
              )}

              {ended && (
                <Box sx={{
                  position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: 'rgba(0,0,0,0.5)',
                }}>
                  <IconButton onClick={handleReplay} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)' }}>
                    <Replay sx={{ fontSize: 40 }} />
                  </IconButton>
                </Box>
              )}

              {/* Custom control bar */}
              <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(0,0,0,0.85)' }}>
                <Slider
                  size="small"
                  min={0}
                  max={duration || 0}
                  value={Math.min(currentTime, duration || 0)}
                  onChange={handleSeek}
                  sx={{ color: BRAND, mb: 0.5, height: 4 }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton size="small" onClick={togglePlay} sx={{ color: '#fff' }}>
                    {playing ? <Pause /> : <PlayArrow />}
                  </IconButton>
                  <IconButton size="small" onClick={() => skip(-10)} sx={{ color: '#fff' }}>
                    <Replay10 fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => skip(10)} sx={{ color: '#fff' }}>
                    <Forward10 fontSize="small" />
                  </IconButton>

                  <Typography variant="caption" sx={{ color: '#fff', minWidth: 90, textAlign: 'center' }}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </Typography>

                  <Box sx={{ flexGrow: 1 }} />

                  <IconButton size="small" onClick={toggleMute} sx={{ color: '#fff' }}>
                    {muted || volume === 0 ? <VolumeOff fontSize="small" /> : <VolumeUp fontSize="small" />}
                  </IconButton>
                  <Slider
                    size="small"
                    min={0}
                    max={1}
                    step={0.05}
                    value={muted ? 0 : volume}
                    onChange={handleVolumeChange}
                    sx={{ color: '#fff', width: 80 }}
                  />

                  <Tooltip title="Playback speed">
                    <IconButton size="small" onClick={(e) => setSpeedMenuAnchor(e.currentTarget)} sx={{ color: '#fff' }}>
                      <Speed fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Menu anchorEl={speedMenuAnchor} open={Boolean(speedMenuAnchor)} onClose={() => setSpeedMenuAnchor(null)}>
                    {SPEEDS.map(s => (
                      <MenuItem key={s} selected={s === speed} onClick={() => handleSpeedSelect(s)}>
                        {s}x
                      </MenuItem>
                    ))}
                  </Menu>

                  <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                    <IconButton size="small" onClick={handleFullscreen} sx={{ color: '#fff' }}>
                      {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
